/*
 * Copyright (c) 2018 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain proprietary information of OCLC Online Computer
 * Library Center, Inc. and shall not be disclosed in whole or in any part to any third party or used by any person
 * for any purpose, without written consent of OCLC, Inc.  Duplication of any portion of these materials shall include
 * this notice.
 */
import './polyfills';

import React from 'react';
import ReactDOM from 'react-dom';
import {IntlProvider, addLocaleData} from 'react-intl';
import {resolveLocale, resolveLanguage, resolveMessages} from 'eui-components';
import {BrowserRouter} from 'react-router-dom';

import {ScrollToTop} from './ScrollToTop';
import App from './App';

import 'eui-bootstrap/build/css/staff.css';
import 'eui-components/build/css/staff.css';
import './global.css';

import 'react-select/dist/react-select.css';

import {api} from './api';
import { Provider } from 'react-redux';

import {configureStore} from './store';

/**
 * Loads the initial state from the server.
 *
 * @returns {Promise.<TResult>}
 */
const loadInitialState = () => {
  return api('/api/state')
    .then(response => response.json())
    .then((data) => {
      return data;
    })
    .catch(() => {
      console.warn('Unable to load initial state from server!');
      console.warn('Mocking initial state from server...');
    });
};

const supportedLanguages = {
  'cs': require('react-intl/locale-data/cs'),
  'de': require('react-intl/locale-data/de'),
  'en': require('react-intl/locale-data/en'),
  'es': require('react-intl/locale-data/es'),
  'fr': require('react-intl/locale-data/fr'),
  'it': require('react-intl/locale-data/it'),
  'mi': {locale: 'mi', parentLocale: 'en'},
  'nl': require('react-intl/locale-data/nl'),
  'pt': require('react-intl/locale-data/pt'),
  'sv': require('react-intl/locale-data/sv'),
  'zh': require('react-intl/locale-data/zh'),
  'ja': require('react-intl/locale-data/ja'),
  'ko': require('react-intl/locale-data/ko'),
  'th': require('react-intl/locale-data/th')
};

/**
 * Renders the application using the provided initial state.
 *
 * @param initialState the initial state.
 */
function render (initialState = {}) {
  console.debug('Rendering...');

  let store = configureStore(initialState);

  const locale = resolveLocale();
  const language = resolveLanguage(locale);
  addLocaleData(supportedLanguages[language]);

  const messages = resolveMessages(locale,
    locale => require(`./messages/messages_${locale}.json`),
    locale => require(`eui-components/build/messages/${locale}`)
  );

  ReactDOM.render(
    <Provider store={store}>
      <IntlProvider locale={locale} messages={messages}>
        <BrowserRouter {...process.env.PUBLIC_URL ? {basename: process.env.PUBLIC_URL} : {}}>
          <ScrollToTop>
            <App />
          </ScrollToTop>
        </BrowserRouter>
      </IntlProvider>
    </Provider>,
    document.getElementById('root'));
}

if (process.env.NODE_ENV === 'development') {
  console.debug('Loading state...');
  loadInitialState().then((state) => {
    render(state);
  });
} else {
  const state = JSON.parse(document.querySelector('body').dataset.initialState);
  render(state);
}



// WEBPACK FOOTER //
// ./src/index.js